// src/components/UserPicker.tsx
// GitLab-style user picker dropdown with search, avatars, and display names.
//
// Who can be picked depends on where you are. Assigning a task inside a project
// should offer that project's members, not every account on the platform, so the
// picker takes an optional `scope` — the parent entity the surrounding form is
// creating under — and lists `ListProjectMembers` / `ListTeamMembers` for it.
// The platform-wide list is the fallback for when there is no parent, never a
// silent substitute for a scope that returned nothing: the dropdown always says
// which set it is showing, and offers everyone only when asked.

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { observer } from '@react';
import { useUserprofile, useUser, useI18n } from '@react/hooks';
import { ListProjectMembers } from '@core/api/endpoints/project/list_project_members';
import { ListTeamMembers } from '@core/api/endpoints/team/list_team_members';
import { getInitials, getAvatarColor } from '@/features/task/logic';

/**
 * The entity the surrounding form is working under. `project` and `team` narrow
 * the candidate list; any other type (or none) leaves it platform-wide, because
 * inventing a membership relation that the backend does not have would offer a
 * list nobody can trust.
 */
export interface UserPickerScope {
  /** Entity type name — 'project' and 'team' are the scoping ones. */
  typeName: string;
  id: string;
  /** How that entity reads in prose, for the "showing members of …" line. */
  label?: string;
}

interface UserPickerProps {
  value: string;
  onChange: (userId: string, displayName: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  className?: string;
  /** When set, shows project members instead of all users */
  projectId?: string;
  /** When set, shows team members instead of all users */
  teamId?: string;
  /** Parent entity to scope the list by — an alternative to projectId/teamId. */
  scope?: UserPickerScope;
}

/** Build a readable name for a profile, falling back to username/email instead of UUID. */
function getProfileLabel(profile: any, userMap: Map<string, any>): string {
  if (profile.display_name) return profile.display_name;
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
  if (fullName) return fullName;
  const userId = profile.user?.id;
  if (userId) {
    const user = userMap.get(userId);
    if (user?.username) return user.username;
    if (user?.email) return user.email;
  }
  return profile.entity?.id?.substring(0, 8) ?? '?';
}

export const UserPicker = observer(({
  value,
  onChange,
  label,
  placeholder,
  error,
  className = '',
  projectId,
  teamId,
  scope,
}: UserPickerProps) => {
  const profileStore = useUserprofile();
  const userStore = useUser();
  const { t } = useI18n();

  // An explicit projectId/teamId wins over `scope`; both express the same idea,
  // and the explicit one is the caller being specific on purpose.
  const scopedProjectId = projectId || (scope?.typeName === 'project' ? scope.id : '') || '';
  const scopedTeamId = teamId || (scope?.typeName === 'team' ? scope.id : '') || '';
  const scopeLabel = scope?.label || '';

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [scopedMembers, setScopedMembers] = useState<any[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(false);
  /** Set when the person deliberately widened an empty scoped list. */
  const [showEveryone, setShowEveryone] = useState(false);
  const [membersNonce, setMembersNonce] = useState(0);

  const hasScope = !!(scopedProjectId || scopedTeamId) && !showEveryone;
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // A different scope is a different list — never show the previous one while
  // the new one loads, and never keep a widened list across a scope change.
  useEffect(() => {
    setScopedMembers([]);
    setMembersError(false);
    setShowEveryone(false);
  }, [scopedProjectId, scopedTeamId]);

  // Load project members (direct API call to avoid store.isLoading side-effects)
  useEffect(() => {
    if (!scopedProjectId) return;
    let cancelled = false;
    setMembersLoading(true);
    setMembersError(false);
    ListProjectMembers(scopedProjectId)
      .then((response) => {
        if (!cancelled) setScopedMembers(response?.members?.members ?? []);
      })
      .catch(() => {
        if (!cancelled) setMembersError(true);
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });
    return () => { cancelled = true; };
  }, [scopedProjectId, membersNonce]);

  // Load team members (direct API call to avoid store.isLoading side-effects)
  useEffect(() => {
    if (!scopedTeamId) return;
    let cancelled = false;
    setMembersLoading(true);
    setMembersError(false);
    ListTeamMembers(scopedTeamId)
      .then((response) => {
        if (!cancelled) {
          const members = (response as any)?.users?.users ?? (response as any)?.members ?? [];
          setScopedMembers(Array.isArray(members) ? members : []);
        }
      })
      .catch(() => {
        if (!cancelled) setMembersError(true);
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });
    return () => { cancelled = true; };
  }, [scopedTeamId, membersNonce]);

  // Profiles and users back two different needs: they *are* the list when there
  // is no scope, and they resolve names for project members (whose membership
  // record carries only a profile entity id).
  const needsProfiles = !hasScope || !!scopedProjectId;
  useEffect(() => {
    if (!isOpen) return;
    if (needsProfiles && profileStore.items.length === 0 && !profileStore.isLoading) {
      profileStore.ListUserprofiles({ sort: [], pagination: { limit: 200, cursor: '', direction: 'forward' } });
    }
    if (!hasScope && userStore.items.length === 0 && !userStore.isLoading) {
      userStore.ListUsers({ sort: [], pagination: { limit: 50, cursor: '', direction: 'forward' } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasScope, needsProfiles]);

  // Map user_id → User for quick lookup (username, email fallbacks)
  const userMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const u of userStore.items || []) {
      if (u.entity?.id) map.set(u.entity.id, u);
    }
    return map;
  }, [userStore.items]);

  // Map profile entity id → Userprofile, to give project members a name.
  const profileByEntityId = useMemo(() => {
    const map = new Map<string, any>();
    for (const p of (profileStore.items || []) as any[]) {
      if (p.entity?.id) map.set(p.entity.id, p);
    }
    return map;
  }, [profileStore.items]);

  // Build unified items list: scoped members, or every profile.
  const allItems = useMemo(() => {
    if (hasScope) {
      return scopedMembers.map((m: any) => {
        // A project member carries `profile` (an entity reference); a team
        // member carries `user` (an actor, name included).
        const actor = m.user && (m.user.display_name !== undefined || m.user.entity) ? m.user : null;
        const profileRef = m.profile || null;
        const hydrated = profileRef?.id ? profileByEntityId.get(profileRef.id) : null;
        const userId =
          actor?.entity?.id || hydrated?.user?.id || profileRef?.id || m.entity?.id || '';
        return {
          _source: 'member' as const,
          user: { id: userId },
          entity: hydrated?.entity || actor?.entity || profileRef || { id: userId },
          display_name: hydrated?.display_name || actor?.display_name || '',
          first_name: hydrated?.first_name,
          last_name: hydrated?.last_name,
          email: hydrated?.email,
          _raw: m,
        };
      });
    }
    return (profileStore.items || []).map((p: any) => ({
      _source: 'profile' as const,
      ...p,
    }));
  }, [hasScope, scopedMembers, profileStore.items, profileByEntityId]);

  // Focus search on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
    }
  }, [isOpen]);

  // Update dropdown position
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxH = 320;
    const openAbove = spaceBelow < maxH && rect.top > spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 320),
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      maxHeight: Math.min(maxH, openAbove ? rect.top - 8 : spaceBelow - 8),
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => updatePosition();
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, updatePosition]);

  // Close on Escape. Bound on the document rather than on a wrapping <div>,
  // because the dropdown is portaled out of it — a container handler would
  // both miss the key and make a plain container pretend to be interactive.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // This picker often sits inside another popover; it closes first.
      e.stopPropagation();
      setIsOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Filter items by search (name, email, username)
  const filtered = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter((p: any) => {
      if ((p.display_name || '').toLowerCase().includes(q)) return true;
      if ((p.first_name || '').toLowerCase().includes(q)) return true;
      if ((p.last_name || '').toLowerCase().includes(q)) return true;
      if ((p.email || '').toLowerCase().includes(q)) return true;
      const user = userMap.get(p.user?.id);
      if (user?.username?.toLowerCase().includes(q)) return true;
      if (user?.email?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [allItems, search, userMap]);

  // Find selected item
  const selected = useMemo(() => {
    if (!value) return null;
    return allItems.find((p: any) =>
      p.user?.id === value || p.entity?.id === value,
    ) as any | null;
  }, [value, allItems]);

  const displayName = selected ? getProfileLabel(selected, userMap) : '';
  const selectedFullName = selected ? [selected.first_name, selected.last_name].filter(Boolean).join(' ') : '';
  const selectedUsername = selectedFullName && selectedFullName !== displayName ? selectedFullName : '';
  const initials = displayName ? getInitials(displayName) : '?';
  const avatarColor = displayName ? getAvatarColor(displayName) : 'hsl(220 14% 50%)';

  const handleSelect = (profile: any) => {
    const userId = profile.user?.id || profile.entity?.id || '';
    const name = getProfileLabel(profile, userMap);
    onChange(userId, name);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1.5">
          {label}
        </label>
      )}

      {/* Trigger. Clearing is a button beside it, not a click target painted on
          an icon: an <svg onClick> inside a <button> is unreachable by keyboard
          and announces nothing. */}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className={`
            w-full flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors
            ${error
              ? 'border-destructive focus:ring-destructive'
              : 'border-input focus:ring-ring'
            }
            ${isOpen ? 'ring-2 ring-ring' : ''}
            ${value ? 'pr-9' : ''}
            bg-card text-foreground hover:bg-muted/50
          `}
        >
          {value ? (
            <>
              <div
                className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-medium text-white flex-shrink-0"
                style={{ backgroundColor: avatarColor }}
              >
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate leading-tight">
                  {displayName}
                </p>
                {selectedUsername && (
                  <p className="text-xs text-muted-foreground truncate leading-tight">
                    {selectedUsername}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <svg
                className="h-5 w-5 text-muted-foreground flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span className="flex-1 text-muted-foreground">
                {placeholder || t('task.field.assigneePlaceholder')}
              </span>
              <svg
                className="h-4 w-4 text-muted-foreground flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </>
          )}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange('', '')}
            aria-label={t('common.clear')}
            title={t('common.clear')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {error && (
        <p className="mt-1 text-xs text-destructive">{error}</p>
      )}

      {/* Dropdown via portal */}
      {isOpen && createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="z-50 rounded-md border border-border bg-popover shadow-lg overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-input bg-background pl-8 pr-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder={t('common.search')}
              />
            </div>
          </div>

          {/* Which set of people is on offer — never left to be guessed. */}
          <p className="border-b border-border px-3 py-1.5 text-xs text-muted-foreground">
            {!hasScope
              ? t('userPicker.fromPlatform')
              : scopedProjectId
                ? scopeLabel
                  ? t('userPicker.fromProject', { name: scopeLabel })
                  : t('userPicker.fromProjectGeneric')
                : scopeLabel
                  ? t('userPicker.fromTeam', { name: scopeLabel })
                  : t('userPicker.fromTeamGeneric')}
          </p>

          {/* Results */}
          <div className="overflow-y-auto py-1" style={{ maxHeight: 'calc(100% - 74px)' }}>
            {(hasScope ? membersLoading : profileStore.isLoading) ? (
              <div className="px-3 py-4 text-center">
                <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <p className="text-xs text-muted-foreground mt-1">{t('common.loading')}</p>
              </div>
            ) : hasScope && membersError ? (
              <div className="px-3 py-4 text-center">
                <p className="text-sm text-destructive">{t('userPicker.loadFailed')}</p>
                <button
                  type="button"
                  onClick={() => setMembersNonce((n) => n + 1)}
                  className="mt-1 rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('common.retry')}
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-center">
                <p className="text-sm text-muted-foreground">
                  {search
                    ? t('common.noResults')
                    : hasScope
                      ? t('userPicker.emptyScope')
                      : t('task.unassigned')}
                </p>
                {hasScope && !search && (
                  <button
                    type="button"
                    onClick={() => setShowEveryone(true)}
                    className="mt-1 rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t('userPicker.showEveryone')}
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Unassign option */}
                {value && (
                  <button
                    type="button"
                    onClick={() => { onChange('', ''); setIsOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted transition-colors"
                  >
                    <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </div>
                    <span className="text-sm text-muted-foreground">{t('task.unassigned')}</span>
                  </button>
                )}

                {filtered.map((profile: any) => {
                  const pId = profile.user?.id || profile.entity?.id || '';
                  const pDisplayName = getProfileLabel(profile, userMap);
                  const pFullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
                  const user = userMap.get(profile.user?.id);
                  const email = profile.email || user?.email || '';
                  const pSecondary = email
                    ? email
                    : pFullName && pFullName !== pDisplayName
                      ? pFullName
                      : user?.username && user.username !== pDisplayName
                        ? `@${user.username}`
                        : '';
                  const pInitials = getInitials(pDisplayName);
                  const pColor = getAvatarColor(pDisplayName);
                  const isSelected = pId === value;

                  return (
                    <button
                      key={pId}
                      type="button"
                      onClick={() => handleSelect(profile)}
                      className={`
                        w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                        ${isSelected ? 'bg-primary/5' : 'hover:bg-muted'}
                      `}
                    >
                      <div
                        className="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-medium text-white flex-shrink-0"
                        style={{ backgroundColor: pColor }}
                      >
                        {pInitials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-foreground truncate">{pDisplayName}</span>
                          {isSelected && (
                            <svg className="h-4 w-4 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          )}
                        </div>
                        {pSecondary && (
                          <p className="text-xs text-muted-foreground truncate">{pSecondary}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

export default UserPicker;
